import { useEffect, useRef, useState } from "react";

/** Counts once on mount. Later updates appear immediately with their real value. */
export function CountUp({
  value,
  format = String,
  className,
}: {
  value: number;
  format?: (value: number) => string;
  className?: string;
}) {
  const [displayed, setDisplayed] = useState(value);
  const arrived = useRef(false);

  useEffect(() => {
    if (arrived.current) {
      setDisplayed(value);
      return;
    }
    arrived.current = true;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduced.matches || document.documentElement.dataset.motion === "reduced") return;

    let frame = 0;
    const started = performance.now();
    const finish = () => {
      cancelAnimationFrame(frame);
      setDisplayed(value);
    };
    const tick = (now: number) => {
      const elapsed = Math.min((now - started) / 520, 1);
      setDisplayed(Math.round(value * (1 - Math.pow(1 - elapsed, 3))));
      if (elapsed < 1) frame = requestAnimationFrame(tick);
    };
    setDisplayed(0);
    frame = requestAnimationFrame(tick);
    reduced.addEventListener("change", finish);
    const observer = new MutationObserver(() => {
      if (document.documentElement.dataset.motion === "reduced") finish();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-motion"] });
    return () => {
      cancelAnimationFrame(frame);
      reduced.removeEventListener("change", finish);
      observer.disconnect();
    };
  }, [value]);

  return (
    <span className={className}>
      <span aria-hidden>{format(displayed)}</span>
      <span className="sr-only">{format(value)}</span>
    </span>
  );
}
